/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from 'react'
import {
  Button,
  TextField,
  Stack,
  Typography,
  Box,
  Modal,
  IconButton,
  Tooltip,
  Checkbox,
} from '@mui/material'
import Select, { SingleValue } from 'react-select'
import { toast } from 'react-toastify'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import { useCreateUserRoleMutation, useUpdateUserRoleMutation } from '../../redux/api/roleApi'
import { IUserRole } from '../../types'
import { capitalize, isEmpty } from 'lodash'
import makeAnimated from 'react-select/animated'
import { useGetModuleListQuery } from '../../redux/api/moduleApi'

const styles = {
  container: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '12px',
  },
  headerStyle: {
    borderRadius: '12px',
    display: 'flex',
    p: 2,
    justifyContent: 'center',
    backgroundColor: '#1976d2',
    mb: 4,
  },
  noMargin: {
    margin: '0px!important',
  },
}

interface Option {
  value: string | number
  label: string
}

interface Props {
  userRole?: IUserRole
  onClose: () => void
  refetch: () => void
  applications?: Option[]
  open: boolean
}

interface RoleOption {
  userRoleToApplicationId?: any
  applicationId: number
  userRoleId?: number
  createFlg: boolean
  updateFlg: boolean
  deleteFlg: boolean
}

interface ModuleOption {
  userRoleToModuleId?: any
  moduleId: number
  createFlg: boolean
  updateFlg: boolean
  deleteFlg: boolean
}

const DEFAULT_ROLE_ITEM: RoleOption = {
  applicationId: 0,
  createFlg: false,
  updateFlg: false,
  deleteFlg: false,
}

const DEFAULT_MODULE_ITEM: ModuleOption = {
  moduleId: 0,
  createFlg: false,
  updateFlg: false,
  deleteFlg: false,
}

export const UserRoleForm = ({ onClose, refetch, open, userRole, applications = [] }: Props) => {
  const [roles, setRoles] = React.useState<RoleOption[]>([DEFAULT_ROLE_ITEM])
  const [moduleRoles, setModuleRoles] = React.useState<ModuleOption[]>([DEFAULT_MODULE_ITEM])
  const [newUserRole, setNewUserRole] = React.useState<string>('')
  const [approvalLimit, setApprovalLimit] = React.useState<number>(0)
  const [createRole, { isLoading, isError, error: cError, isSuccess }] = useCreateUserRoleMutation()
  const [
    updateRole,
    { isLoading: uLoading, isError: uIsError, error: uError, isSuccess: uIsSuccess },
  ] = useUpdateUserRoleMutation()

  const { data } = useGetModuleListQuery(null)

  const moduleOptions: Option[] =
    data?.map((mod: any) => ({
      value: mod.moduleId,
      label: mod.moduleName,
    })) || []

  const getApplicationOptions = (roleApplicationId: number) => {
    const prevApplications = roles
      .filter((role) => role.applicationId !== 0 && role.applicationId !== roleApplicationId)
      .map((role) => role.applicationId)

    return applications.filter(
      (application) => !prevApplications.includes(Number(application.value)),
    )
  }

  const getModuleOptions = (selectedModuleId: number) => {
    const selectedModuleIds = moduleRoles
      .filter((mod) => mod.moduleId !== 0 && mod.moduleId !== selectedModuleId)
      .map((mod) => mod.moduleId)

    return moduleOptions.filter((mod) => !selectedModuleIds.includes(Number(mod.value)))
  }

  useEffect(() => {
    setNewUserRole(userRole?.userRole ?? '')
    setApprovalLimit(userRole?.approvalLimit ?? 0)

    if (userRole?.applications && Array.isArray(userRole.applications)) {
      setRoles(
        userRole.applications.map((app) => ({
          userRoleToApplicationId: app.userRoleToApplicationId,
          applicationId:
            app.applicationId ||
            (applications.find((a) => a.label === app.applicationName)?.value as number) ||
            0,
          createFlg: app.create === 1,
          updateFlg: app.update === 1,
          deleteFlg: app.delete === 1,
        })),
      )
    } else {
      setRoles([DEFAULT_ROLE_ITEM])
    }

    if (userRole?.modules && Array.isArray(userRole.modules)) {
      setModuleRoles(
        userRole.modules.map((mod) => ({
          userRoleToModuleId: mod.userRoleToModuleId,
          moduleId: mod.moduleId,
          createFlg: mod.create === 1,
          updateFlg: mod.update === 1,
          deleteFlg: mod.delete === 1,
        })),
      )
    } else {
      setModuleRoles([DEFAULT_MODULE_ITEM])
    }
  }, [userRole, applications])

  useEffect(() => {
    if (isSuccess || uIsSuccess) {
      toast.success('Success!')
      onClose()
      refetch()
    }
    if (isError || uIsError) {
      const error = isEmpty(cError) ? uError : cError
      if (Array.isArray((error as any)?.data?.error)) {
        const errors = error as any
        errors?.data.error.forEach((el: any) =>
          toast.error(el?.message ?? 'Something Failed!', {
            position: 'top-right',
          }),
        )
      } else {
        toast.error((error as any)?.data?.message ?? 'Something Failed!', {
          position: 'top-right',
        })
      }
    }
  }, [isLoading, uLoading])

  const animatedComponents = makeAnimated()

  const handleSubmit = () => {
    const validRoles = roles
      .filter((role) => role.applicationId !== 0)
      .map((role) => ({
        userRoleToApplicationId: role.userRoleToApplicationId,
        applicationId: role.applicationId,
        createFlg: role.createFlg,
        updateFlg: role.updateFlg,
        deleteFlg: role.deleteFlg,
      }))

    const validModules = moduleRoles
      .filter((mod) => mod.moduleId !== 0)
      .map((mod) => ({
        moduleId: mod.moduleId,
        createFlg: mod.createFlg,
        updateFlg: mod.updateFlg,
        deleteFlg: mod.deleteFlg,
      }))

    const roleData = {
      userRole: capitalize(newUserRole),
      approvalLimit: approvalLimit,
      roles: validRoles,
      modules: validModules,
    }

    if (userRole?.userRoleId) {
      updateRole({
        userRoleId: userRole.userRoleId,
        ...roleData,
      })
    } else {
      createRole(roleData)
    }
  }

  const handleChange = (value: string) => {
    setNewUserRole(value)
  }

  const handleApprovalChange = (value: string) => {
    const numValue = Number(value)
    if (!isNaN(numValue)) {
      setApprovalLimit(numValue)
    } else {
      setApprovalLimit(0)
    }
  }

  const addOption = () => {
    if (roles.length >= applications.length) {
      return
    }
    setRoles([...roles, DEFAULT_ROLE_ITEM])
  }

  const removeRole = (idx: number) => {
    if (roles.length === 1) {
      return
    }
    setRoles([...roles.slice(0, idx), ...roles.slice(idx + 1)])
  }

  const addModuleOption = () => {
    if (moduleRoles.length >= moduleOptions.length) {
      return
    }
    setModuleRoles([...moduleRoles, DEFAULT_MODULE_ITEM])
  }

  const removeModuleRole = (idx: number) => {
    if (moduleRoles.length === 1) {
      return
    }
    setModuleRoles([...moduleRoles.slice(0, idx), ...moduleRoles.slice(idx + 1)])
  }

  const reset = () => {
    onClose()
    if (!userRole) {
      setRoles([DEFAULT_ROLE_ITEM])
      setModuleRoles([DEFAULT_MODULE_ITEM])
    }
  }

  return (
    <Modal
      open={open}
      onClose={reset}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Box sx={styles.container}>
        <Box sx={styles.headerStyle}>
          <Typography id='modal-modal-title' variant='h5' component='h2' color='white'>
            User Role Details
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              value={newUserRole}
              placeholder='Name'
              onChange={(event) => handleChange(event.target.value)}
            />
          </Stack>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              value={approvalLimit}
              type='number'
              placeholder='Approval Limit'
              onChange={(event) => handleApprovalChange(event.target.value)}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Typography variant='h6' component='h5'>
                Applications
              </Typography>
              <IconButton onClick={addOption}>
                <AddCircleIcon color='primary' />
              </IconButton>
            </Box>
            <Typography
              sx={styles.noMargin}
              variant='subtitle1'
              component='span'
              align='center'
              color='lightgrey'
            >
              Add applications with roles and select permissions.
            </Typography>
            {roles.map((role, idx) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }} key={`role-${idx}`}>
                <Select
                  closeMenuOnSelect
                  components={animatedComponents}
                  placeholder='Application'
                  value={
                    applications.find((application) => application.value === role.applicationId) ||
                    null
                  }
                  onChange={(newValue, _actionMeta) => {
                    setRoles([
                      ...roles.slice(0, idx),
                      {
                        ...role,
                        applicationId:
                          newValue && 'value' in newValue ? (newValue.value as number) : 0,
                      },
                      ...roles.slice(idx + 1),
                    ])
                  }}
                  options={getApplicationOptions(role.applicationId)}
                  className='react-select-container'
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      height: '56px',
                      flex: 1,
                    }),
                  }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography variant='caption'>Create</Typography>
                  <Checkbox
                    checked={role.createFlg}
                    inputProps={{ 'aria-label': 'Create permission' }}
                    onChange={(e) => {
                      setRoles([
                        ...roles.slice(0, idx),
                        { ...role, createFlg: e.target.checked },
                        ...roles.slice(idx + 1),
                      ])
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography variant='caption'>Update</Typography>
                  <Checkbox
                    checked={role.updateFlg}
                    inputProps={{ 'aria-label': 'Update permission' }}
                    onChange={(e) => {
                      setRoles([
                        ...roles.slice(0, idx),
                        { ...role, updateFlg: e.target.checked },
                        ...roles.slice(idx + 1),
                      ])
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography variant='caption'>Delete</Typography>
                  <Checkbox
                    checked={role.deleteFlg}
                    inputProps={{ 'aria-label': 'Delete permission' }}
                    onChange={(e) => {
                      setRoles([
                        ...roles.slice(0, idx),
                        { ...role, deleteFlg: e.target.checked },
                        ...roles.slice(idx + 1),
                      ])
                    }}
                  />
                </Box>
                <Tooltip title='Remove Role' arrow>
                  <IconButton onClick={() => removeRole(idx)}>
                    <CancelIcon color='error' />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Stack>

          {/* Modules Section */}
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Typography variant='h6' component='h5'>
                Modules
              </Typography>
              <IconButton onClick={addModuleOption}>
                <AddCircleIcon color='primary' />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <Typography
                sx={{ ...styles.noMargin, textAlign: 'center', width: '100%' }}
                variant='subtitle1'
                color='lightgrey'
              >
                Add modules with roles and select permissions.
              </Typography>
            </Box>

            {moduleRoles.map((modRole, idx) => (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: 1 }}
                key={`module-${idx}`}
              >
                <Select
                  closeMenuOnSelect
                  components={animatedComponents}
                  placeholder='Module'
                  value={moduleOptions.find((module) => module.value === modRole.moduleId) || null}
                  onChange={(newValue, _actionMeta) => {
                    setModuleRoles([
                      ...moduleRoles.slice(0, idx),
                      {
                        ...modRole,
                        moduleId: newValue && 'value' in newValue ? (newValue.value as number) : 0,
                      },
                      ...moduleRoles.slice(idx + 1),
                    ])
                  }}
                  options={getModuleOptions(modRole.moduleId)}
                  className='react-select-container'
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      height: '56px',
                      flex: 1,
                    }),
                  }}
                />

                {['createFlg', 'updateFlg', 'deleteFlg'].map((perm) => (
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                    key={perm}
                  >
                    <Typography variant='caption' sx={{ textTransform: 'capitalize' }}>
                      {perm.replace('Flg', '')}
                    </Typography>
                    <Checkbox
                      checked={modRole[perm as keyof ModuleOption] as boolean}
                      inputProps={{ 'aria-label': `${perm} permission` }}
                      onChange={(e) => {
                        setModuleRoles([
                          ...moduleRoles.slice(0, idx),
                          { ...modRole, [perm]: e.target.checked },
                          ...moduleRoles.slice(idx + 1),
                        ])
                      }}
                    />
                  </Box>
                ))}
                <Tooltip title='Remove Module' arrow>
                  <IconButton onClick={() => removeModuleRole(idx)}>
                    <CancelIcon color='error' />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>

          <Box sx={{ textAlign: 'right', mt: '30px' }}>
            <Button
              variant='outlined'
              size='large'
              sx={{ mr: 2, borderRadius: '20px' }}
              color='error'
              onClick={reset}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              sx={{ borderRadius: '20px' }}
              size='large'
              onClick={handleSubmit}
              disabled={
                isEmpty(newUserRole) ||
                isLoading ||
                uLoading ||
                !roles.some((role) => role.applicationId !== 0)
              }
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}
